import React from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AccountDetails } from '../../../types';

import { ADMINISTRATION_ACCOUNTS } from 'RestAPIs/endpoints';

import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';

import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import { yupResolver } from '@hookform/resolvers/yup';
import { accountSchema } from './utils/schema';
import { useCreateResource } from 'RestAPIs/useCreateResource';
import {
  AccountDetailAccountName,
  AccountDetailComEmail,
  AccountDetailEmpId,
  AccountDetailNameFirst,
  AccountDetailNameLast,
  AccountDetailValidFrom,
  AccountDetailValidTo
} from './AccountDetailsFormFields';
import { useGetLinkedEmployeeOptions } from './hooks/useGetLinkedEmployeeOptions';
import { isEmpty } from 'ramda';

interface Props {
  onClose: () => void;
}

export const AccountForm: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const { data: options } = useGetLinkedEmployeeOptions(true);

  const methods = useForm<AccountDetails>({
    resolver: yupResolver(accountSchema),
    defaultValues: accountSchema.cast({})
  });
  const { mutateAsync: createForm, isLoading } = useCreateResource({
    url: ADMINISTRATION_ACCOUNTS,
    invalidateQueryKey: ['accounts', 'list']
  });

  const handleSubmit = async (formData: AccountDetails) => {
    await createForm({
      ...formData,
      empId: isEmpty(formData.empId) ? undefined : formData.empId
    } as AccountDetails);
    onClose();
  };

  return (
    <MultiStepForm
      methods={methods}
      onSubmit={methods.handleSubmit(handleSubmit)}
    >
      <Box display="flex" flexDirection="column" gap={2.5} marginTop={2}>
        <AccountDetailAccountName control={methods.control} />
        <AccountDetailComEmail control={methods.control} />
        <AccountDetailEmpId control={methods.control} options={options} />
        <AccountDetailNameFirst control={methods.control} />
        <AccountDetailNameLast control={methods.control} />
        <AccountDetailValidFrom control={methods.control} />
        <AccountDetailValidTo control={methods.control} />
      </Box>
      <MultiStepForm.Actions>
        <Box display="flex" ml="auto">
          <LoadingButton
            loading={isLoading}
            type="submit"
            variant="contained"
            autoFocus
            color="dark"
          >
            {t('save')}
          </LoadingButton>
        </Box>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};
